import { HtmlHTMLAttributes, useCallback, useMemo } from 'react';
import {
  Avatar as BaseAvatar,
  AvatarFallback,
  AvatarImage,
  cn,
  AvatarUploadOverlay,
  AvatarUploadSettings,
} from '@sib/ui';
import { getInitials } from '@sib/utils';
import { User } from '../icons';

export interface AvatarProps extends HtmlHTMLAttributes<HTMLDivElement> {
  className?: string;
  avatarImageProps: {
    avatarUrl?: string;
  } & HtmlHTMLAttributes<HTMLImageElement>;
  avatarFallbackProps: {
    initials?: string;
    name?: string;
  } & HtmlHTMLAttributes<HTMLSpanElement>;
  uploadSettings?: AvatarUploadSettings;
  variant?: 'default' | 'small';
}

export const UserAvatar = ({
  avatarImageProps,
  avatarFallbackProps,
  uploadSettings,
  variant = 'default',
  ...props
}: AvatarProps) => {
  const getAvatarInitials = useCallback(
    (name?: string) =>
      variant === 'small'
        ? name?.at(0) || <User className="size-2.5" />
        : getInitials(name),
    [variant]
  );

  const commonAvatarImageProps = useMemo(
    () => ({
      ...avatarImageProps,
      src: avatarImageProps?.avatarUrl,
      alt: avatarFallbackProps?.name,
      className: avatarImageProps?.className,
    }),
    [avatarImageProps, avatarFallbackProps]
  );

  const initials = useMemo(() => {
    if (avatarFallbackProps?.initials) return avatarFallbackProps.initials;

    return getAvatarInitials(avatarFallbackProps?.name);
  }, [avatarFallbackProps, getAvatarInitials]);

  if (variant === 'default')
    return (
      <BaseAvatar {...props} className={cn('group size-24', props.className)}>
        {uploadSettings && (
          <AvatarUploadOverlay
            IconProps={{ className: 'size-5' }}
            {...uploadSettings}
          />
        )}
        <AvatarImage
          {...commonAvatarImageProps}
          className={cn(
            'h-full w-full object-cover object-center',
            avatarImageProps?.className
          )}
        />
        <AvatarFallback
          className={cn('text-xl font-bold', avatarFallbackProps?.className)}
        >
          {initials}
        </AvatarFallback>
      </BaseAvatar>
    );

  return (
    <BaseAvatar
      className={cn(
        'group outline-border-strong ml-0.5 size-5 outline',
        props.className
      )}
    >
      {uploadSettings && <AvatarUploadOverlay {...uploadSettings} />}
      <AvatarImage {...commonAvatarImageProps} />
      <AvatarFallback>{initials}</AvatarFallback>
    </BaseAvatar>
  );
};

UserAvatar.displayName = 'UserAvatar';
